import styled, { css } from 'styled-components/macro';

type TypographyStyledT = {
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?:
    | 'black'
    | 'light'
    | 'darkBlue'
    | 'primaryText'
    | 'lightGrey'
    | 'grey'
    | 'darkGrey'
    | 'dark'
    | 'purple'
    | 'yellow'
    | 'success'
    | 'warning'
    | 'error'
    | 'semiTransparent'
    | 'darkGreen'
    | 'darkPurple'
    | 'red';
  align?: 'left' | 'right' | 'center';
  family?: 'primary' | 'secondary';
  variant?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'body1'
    | 'body2'
    | 'body3'
    | 'body4'
    | 'body5'
    | 'body6';
  weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  isInline?: boolean;
};

const isInlineStyles = css`
  display: inline-block;
`;

const xxs = css`
  font-size: ${({ theme }): string => theme.fontSize.xxs};
  line-height: 13px;
`;

const xs = css`
  font-size: ${({ theme }): string => theme.fontSize.xs};
  line-height: 14px;
`;

const sm = css`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  line-height: 15px;
`;

const md = css`
  font-size: ${({ theme }): string => theme.fontSize.md};
  line-height: 16px;
`;

const lg = css`
  font-size: ${({ theme }): string => theme.fontSize.lg};
  line-height: 17px;
`;

const xl = css`
  font-size: ${({ theme }): string => theme.fontSize.xl};
  line-height: 18px;
`;

const normal = css`
  font-weight: ${({ theme }): string => theme.fontWeight.normal.toString()};
`;

const medium = css`
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
`;

const semibold = css`
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
`;

const bold = css`
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
`;

const black = css`
  color: ${({ theme }): string => theme.colors.black};
`;

const light = css`
  color: ${({ theme }): string => theme.colors.white};
`;

const darkBlue = css`
  color: ${({ theme }): string => theme.colors.primary.variant1};
`;

const lightGrey = css`
  color: ${({ theme }): string => theme.colors.primary.variant3};
`;

const grey = css`
  color: ${({ theme }): string => theme.colors.primary.variant6};
`;

const darkGrey = css`
  color: ${({ theme }): string => theme.colors.primary.variant7};
`;

const dark = css`
  color: ${({ theme }): string => theme.colors.primary.variant9};
`;

const semiTransparent = css`
  color: ${({ theme }): string => theme.colors.white};
  opacity: 0.35;
`;

const purple = css`
  color: ${({ theme }): string => theme.colors.highlight.variant3};
`;

const yellow = css`
  color: ${({ theme }): string => theme.colors.highlight.variant4};
`;

const success = css`
  color: ${({ theme }): string => theme.colors.secondary.variant2};
`;

const warning = css`
  color: ${({ theme }): string => theme.colors.highlight.variant1};
`;

const error = css`
  color: ${({ theme }): string => theme.colors.highlight.variant2};
`;

const red = css`
  color: ${({ theme }): string => theme.colors.highlight.variant2};
`;

const darkGreen = css`
  color: ${({ theme }): string => theme.colors.primary.variant12};
`;

const darkPurple = css`
  color: ${({ theme }): string => theme.colors.primary.variant13};
`;

/** Matches WarrQueue work item header values: theme primary text (variant11). */
const primaryText = css`
  color: ${({ theme }): string => theme.colors.primary.variant11};
`;

const alignLeft = css`
  text-align: left;
`;

const alignRight = css`
  text-align: right;
`;

const alignCenter = css`
  text-align: center;
`;

const h1 = css`
  font-size: ${({ theme }): string => theme.fontSize['5xl']};
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
  line-height: 1;
`;

const h2 = css`
  font-size: ${({ theme }): string => theme.fontSize['4xl']};
  font-weight: ${({ theme }): string => theme.fontWeight.bold.toString()};
  line-height: 1.17;
`;

const h3 = css`
  font-size: ${({ theme }): string => theme.fontSize['3xl']};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  line-height: 1.29;
`;

const h4 = css`
  font-size: ${({ theme }): string => theme.fontSize['2xl']};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  line-height: 1.12;
`;

const h5 = css`
  font-size: ${({ theme }): string => theme.fontSize.xl};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  line-height: 1.13;
`;

const h6 = css`
  font-size: ${({ theme }): string => theme.fontSize.md};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  line-height: 1.143;
`;

const body1 = css`
  font-size: ${({ theme }): string => theme.fontSize.xl};
  font-weight: ${({ theme }): string => theme.fontWeight.normal.toString()};
  line-height: 1.5;
`;

const body2 = css`
  font-size: ${({ theme }): string => theme.fontSize.lg};
  font-weight: ${({ theme }): string => theme.fontWeight.semibold.toString()};
  line-height: 1.54;
`;

const body3 = css`
  font-size: ${({ theme }): string => theme.fontSize.md};
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  line-height: 1.15;
`;

const body4 = css`
  font-size: ${({ theme }): string => theme.fontSize.sm};
  font-weight: ${({ theme }): string => theme.fontWeight.normal.toString()};
  line-height: 1.16;
`;

const body5 = css`
  font-size: ${({ theme }): string => theme.fontSize.xxs};
  font-weight: ${({ theme }): string => theme.fontWeight.medium.toString()};
  line-height: 1;
`;

const primary = css`
  font-family: ${({ theme }): string => theme.fontFamily.primary};
`;

const secondary = css`
  font-family: ${({ theme }): string => theme.fontFamily.secondary};
`;

const body6 = css`
  font-size: ${({ theme }): string => theme.fontSize.xxs};
  font-weight: ${({ theme }): string => theme.fontWeight.normal.toString()};
  line-height: 1;
`;

const textSize = {
  xxs,
  xs,
  sm,
  md,
  lg,
  xl,
};

const textColor = {
  black,
  light,
  darkBlue,
  primaryText,
  lightGrey,
  grey,
  darkGrey,
  dark,
  purple,
  yellow,
  success,
  warning,
  error,
  semiTransparent,
  darkGreen,
  darkPurple,
  red,
};

const textAlign = {
  left: alignLeft,
  right: alignRight,
  center: alignCenter,
};

const textVariant = {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  body1,
  body2,
  body3,
  body4,
  body5,
  body6,
};

const textFamily = {
  primary,
  secondary,
};

const textWeight = {
  normal,
  medium,
  semibold,
  bold,
};

export const TypographyStyled = styled.p<TypographyStyledT>`
  ${({ size }): ReturnType<typeof css> | string => (size ? textSize[size] : '')}
  ${({ align }): ReturnType<typeof css> | string => (align ? textAlign[align] : '')}
  ${({ variant }): ReturnType<typeof css> | string => (variant ? textVariant[variant] : '')}
  ${({ family }): ReturnType<typeof css> | string => (family ? textFamily[family] : '')}
  ${({ color = 'black' }): ReturnType<typeof css> => textColor[color]}
  ${({ weight }): ReturnType<typeof css> | string => (weight ? textWeight[weight] : '')}
  ${({ isInline }): ReturnType<typeof css> | string => (isInline ? isInlineStyles : '')}
`;
